import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {Box, IconButton, InputAdornment, Stack, TextField} from "@mui/material";
import {alpha} from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import {LocalizedText} from "crqlar-react-common/dist/models/common/localizedText.model";
import {getIconForLanguage, getNameForLanguage} from "crqlar-react-common/dist/utils/translations.util";
import {useTranslation} from "react-i18next";

interface Props {
    id: string
    value?: LocalizedText
    languages?: string[]
    onChange: (value: LocalizedText | undefined) => void
    placeholder?: string
    disabled?: boolean
}

const FLAG_WIDTH = 21;
const FLAG_HEIGHT = 14;

const FLAG_FALLBACK_FONT_SIZE = 12;

const SurveyLinkField = ({id, value, languages, onChange, placeholder, disabled}: Props) => {

    const {t} = useTranslation()

    const setLanguage = (language: string, next: string) => {
        const updated: LocalizedText = {...value}

        if (next.trim()) updated[language] = next
        else delete updated[language]

        onChange(Object.keys(updated).length ? updated : undefined)
    }

    const flag = (code: string) => {
        const src = getIconForLanguage(code)

        if (!src) {
            return (
                <Typography sx={{fontSize: FLAG_FALLBACK_FONT_SIZE, fontWeight: 600, color: 'text.secondary'}}>
                    {code.toUpperCase()}
                </Typography>
            )
        }

        return (
            <Box
                component={'img'}
                src={src}
                alt={''}
                sx={theme => ({
                    display: 'block',
                    flexShrink: 0,
                    boxSizing: 'border-box',
                    width: FLAG_WIDTH,
                    height: FLAG_HEIGHT,
                    objectFit: 'cover',
                    borderRadius: 0.5,
                    border: `1px solid ${alpha(theme.palette.text.primary, 0.15)}`,
                })}
            />
        )
    }

    if (!languages?.length) {
        return (
            <Typography variant={'body2'} color={'text.secondary'}>
                {t('Pick the survey languages first - each one gets its own link.')}
            </Typography>
        )
    }

    return (
        <Stack gap={1} sx={{width: '100%'}}>
            {languages.map(language => {
                const link = value?.[language]?.trim()

                return (
                    <TextField
                        key={language}
                        id={`${id}-${language}`}
                        size={'small'}
                        value={value?.[language] || ''}
                        onChange={(e) => setLanguage(language, e.target.value)}
                        placeholder={placeholder}
                        disabled={disabled}
                        fullWidth
                        inputProps={{'aria-label': getNameForLanguage(language, true) || language}}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position={'start'}>
                                    {flag(language)}
                                </InputAdornment>
                            ),
                            endAdornment: !!link && (
                                <InputAdornment position={'end'}>
                                    <IconButton
                                        size={'small'}
                                        component={'a'}
                                        href={link}
                                        target={'_blank'}
                                        rel={'noopener noreferrer'}
                                        aria-label={t('Open link in a new tab')}
                                        edge={'end'}
                                    >
                                        <OpenInNewIcon sx={{fontSize: 16}}/>
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                )
            })}
        </Stack>
    )
}

export default SurveyLinkField
